/**
 * Handwritten on purpose: catalog shaping and resolution-stage helpers on top
 * of generated admin-variables. New REST calls should use getAdminVariables().
 */
import { getAdminVariables } from 'api/generated/api/admin-variables';
import type { BuiltinVariableGroup } from 'api/services/builtinVariableService';
import type {
  CatalogVariable,
  CatalogVariableType,
  DeprecatedVariableMapping,
  ResolutionStage,
  VariableCatalogResponse,
} from 'api/generated/types';
import { isResolutionStageAvailable } from 'utils/variableValueDisplay';

/** Unified catalog returned by GET /admin/variables/catalog. */
export type VariableCatalog = {
  variables: CatalogVariable[];
  deprecated_variables?: DeprecatedVariableMapping[];
  /** False when ATS custom-field definitions were missing or stale. */
  job_schema_complete?: boolean;
};

/** Builtin group keys reconstructed from the flat catalog (mirrors backend GROUP_ORDER). */
const BUILTIN_GROUP_ORDER = [
  'general',
  'job',
  'candidate',
  'application',
  'call',
] as const satisfies readonly CatalogVariableType[];

/** Human-facing section titles (mirrors backend GROUP_TITLES). */
const BUILTIN_GROUP_TITLES: Record<
  (typeof BUILTIN_GROUP_ORDER)[number],
  string
> = {
  general: 'General Variables',
  job: 'Job Variables',
  candidate: 'Candidate Variables',
  application: 'Application Variables',
  call: 'Call Variables',
};

const isBuiltinGroupType = (
  type: CatalogVariableType
): type is (typeof BUILTIN_GROUP_ORDER)[number] =>
  (BUILTIN_GROUP_ORDER as readonly string[]).includes(type);

/**
 * Named wrapper around the organization-variables endpoint. Returns the flat org-level catalog:
 * built-ins, bare job/ATS paths, lookup columns, and custom (derived) names.
 */
export const fetchVariableCatalog = async (): Promise<VariableCatalog> => {
  const response: VariableCatalogResponse =
    await getAdminVariables().adminVariablesListVariableCatalog();
  return {
    variables: response?.variables ?? [],
    deprecated_variables: response?.deprecated_variables ?? [],
    job_schema_complete: response?.job_schema_complete ?? true,
  };
};

/**
 * Lookup ∪ custom names for coloring / validation.
 * Excludes bare `type === 'job'` paths — those live in
 * {@link toJobAttributeVariableNameSet} so they stay available as unnamespaced
 * tails for `job.*` alias validation (and are not treated as CSV lookup keys).
 */
export const toOrganizationVariableNameSet = (
  variables: CatalogVariable[]
): Set<string> =>
  new Set(
    variables
      .filter((v) => v.type === 'lookup' || v.type === 'custom')
      .map((v) => v.name)
  );

/**
 * Lookup ∪ custom names whose catalog ``resolution_stage`` is at or before
 * ``maxStage`` (default: config). Used by Background Info templates, which only
 * resolve at config time and must not offer send-/call-stage customs.
 */
export const toOrganizationVariableNameSetAtOrBeforeStage = (
  variables: CatalogVariable[],
  maxStage: ResolutionStage = 'config'
): Set<string> =>
  new Set(
    variables
      .filter(
        (v) =>
          (v.type === 'lookup' || v.type === 'custom') &&
          isResolutionStageAvailable(v.resolution_stage ?? 'config', maxStage)
      )
      .map((v) => v.name)
  );

/** Custom names at or before ``maxStage`` — subset of the org-name stage filter. */
export const toDerivedVariableNameSetAtOrBeforeStage = (
  variables: CatalogVariable[],
  maxStage: ResolutionStage = 'config'
): Set<string> =>
  new Set(
    variables
      .filter(
        (v) =>
          v.type === 'custom' &&
          isResolutionStageAvailable(v.resolution_stage ?? 'config', maxStage)
      )
      .map((v) => v.name)
  );

/** Custom (derived) variable names, for the "Custom Variables" autocomplete group. */
export const toDerivedVariableNameSet = (
  variables: CatalogVariable[]
): Set<string> =>
  new Set(variables.filter((v) => v.type === 'custom').map((v) => v.name));

/**
 * Bare job/ATS attribute paths from the org schema (`type === 'job'` and name does
 * not start with `job.`). Namespaced `job.*` builtins are excluded.
 */
export const toJobAttributeVariableNameSet = (
  variables: CatalogVariable[]
): Set<string> =>
  new Set(
    variables
      .filter((v) => v.type === 'job' && !v.name.startsWith('job.'))
      .map((v) => v.name)
  );

/**
 * Reconstruct grouped built-ins for autocomplete from the flat catalog.
 *
 * Includes `general` | `job` | `candidate` | `application` | `call` only. For `job`,
 * only namespaced `job.*` names are included (bare ATS paths stay in
 * {@link toJobAttributeVariableNameSet}).
 */
export const toBuiltinVariableGroups = (
  variables: CatalogVariable[]
): BuiltinVariableGroup[] => {
  const byType = new Map<
    (typeof BUILTIN_GROUP_ORDER)[number],
    CatalogVariable[]
  >();
  for (const key of BUILTIN_GROUP_ORDER) {
    byType.set(key, []);
  }

  for (const variable of variables) {
    if (!isBuiltinGroupType(variable.type)) {
      continue;
    }
    if (variable.type === 'job' && !variable.name.startsWith('job.')) {
      continue;
    }
    byType.get(variable.type)?.push(variable);
  }

  return BUILTIN_GROUP_ORDER.map((key) => ({
    key,
    title: BUILTIN_GROUP_TITLES[key],
    variables: (byType.get(key) ?? []).map((v) => ({
      name: v.name,
      group: v.type,
      resolution_stage: v.resolution_stage ?? 'config',
      description: v.description ?? '',
      may_be_absent: v.may_be_absent ?? false,
    })),
  }));
};
